import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { PlannerDayDto, StoreDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { SparkleIcon } from '../../components/icons';
import { Button, buttonClass } from '../../components/Button';
import { Field } from '../../components/Field';
import { StorePicker } from '../../components/groceries/StorePicker';
import { PlannerDayCard } from '../../components/plans/PlannerDayCard';
import { groceryKeys } from '../groceries/queries';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { addDays, formatWeekLabel, isIsoDate, startOfWeek, todayIso } from '../../lib/planDates';
import {
  acceptableDays,
  acceptBody,
  defaultPlanStart,
  lockFor,
  pinsExcept,
  withExcluded,
  type PlannerLock,
} from '../../lib/planner';

const DAYS = 7;

const chipButton = (active: boolean) =>
  `min-h-9 rounded-full px-3.5 text-sm font-semibold transition-colors ${
    active
      ? 'bg-brand text-white shadow-sm'
      : 'border border-line bg-surface text-brand hover:bg-brand-soft'
  }`;

/**
 * "Plan my week" (D29): the planner proposes a main (and optionally a side)
 * for each day that fits the household's routine and busy time; the user
 * swaps, locks or skips days, then adds them all to the plan — optionally
 * building the grocery list for the week in the same step.
 */
export function PlanMyWeekPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = todayIso();

  const [from, setFrom] = useState(() => defaultPlanStart(today));
  const [includeSides, setIncludeSides] = useState(false);
  const [exclude, setExclude] = useState<Record<string, string[]>>({});
  const [userLocks, setUserLocks] = useState<Record<string, PlannerLock>>({});
  /** Other days held still while one day is swapped; cleared when the range changes. */
  const [pins, setPins] = useState<Record<string, PlannerLock>>({});
  const [skip, setSkip] = useState<string[]>([]);
  const [buildList, setBuildList] = useState(true);
  const [choosingStore, setChoosingStore] = useState(false);
  const [store, setStore] = useState<StoreDto | null>(null);

  const request = {
    from,
    days: DAYS,
    slot: 'dinner' as const,
    includeSides,
    exclude,
    locked: { ...pins, ...userLocks },
    skip,
  };
  const proposal = useQuery({
    queryKey: ['planner', 'week', request],
    queryFn: () => api.planner.week(request),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const days = proposal.data?.days ?? [];
  const ready = acceptableDays(days);
  const working = proposal.isFetching;
  // An unconstrained day with nothing to propose means there are no mains at all.
  const noRecipes = days.some(
    (d) => d.skipped?.kind === 'no_recipe_fits' && d.availableMin === null,
  );

  function changeRange(next: string) {
    if (!isIsoDate(next)) return;
    setFrom(next);
    setExclude({});
    setPins({});
    setSkip([]);
    setUserLocks({});
  }

  function swap(day: PlannerDayDto) {
    if (!day.main) return;
    setPins(pinsExcept(days, day.date));
    setExclude((prev) => withExcluded(prev, day.date, day.main!.recipe.recipeId));
  }

  function toggleLock(day: PlannerDayDto) {
    setUserLocks((prev) => {
      const next = { ...prev };
      if (next[day.date]) delete next[day.date];
      else next[day.date] = lockFor(day);
      return next;
    });
  }

  function skipDay(day: PlannerDayDto) {
    setSkip((prev) => (prev.includes(day.date) ? prev : [...prev, day.date]));
    setUserLocks((prev) => {
      const next = { ...prev };
      delete next[day.date];
      return next;
    });
  }

  function restoreDay(day: PlannerDayDto) {
    setSkip((prev) => prev.filter((d) => d !== day.date));
  }

  const accept = useMutation({
    mutationFn: () =>
      api.planner.accept(
        acceptBody(days, buildList ? { create: true, storeId: store?.id ?? null } : null),
      ),
    onSuccess: async (result) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.plans.all }),
        qc.invalidateQueries({ queryKey: ['planner'] }),
        qc.invalidateQueries({ queryKey: groceryKeys.all }),
      ]);
      const n = result.plans.length;
      const toast = `Added ${n} ${n === 1 ? 'meal' : 'meals'} to the plan.`;
      if (result.groceryListId) {
        await navigate(`/groceries/${result.groceryListId}`);
      } else {
        await navigate(`/plan?week=${startOfWeek(from)}`, { state: { toast } });
      }
    },
  });

  const nextMonday = defaultPlanStart(addDays(today, 1));
  const n = ready.length;

  return (
    <AppShell width="wide">
      <div className="flex flex-col gap-4 lg:gap-5">
        <div className="relative flex flex-col gap-1 overflow-hidden rounded-3xl border border-line bg-gradient-to-br from-brand-soft via-surface to-accent-soft p-5 sm:p-6">
          <span
            aria-hidden
            className="pointer-events-none absolute -top-10 -right-10 size-40 rounded-full bg-accent/30"
          />
          <div className="relative flex flex-wrap items-center justify-between gap-2">
            <Link
              to="/plan"
              className="inline-flex min-h-9 items-center text-sm font-medium text-brand underline-offset-2 hover:underline"
            >
              ← Meal plan
            </Link>
            <Link to="/plan/week/auto" className={buttonClass({ size: 'sm', block: false })}>
              <SparkleIcon className="size-4" /> Auto-plan
            </Link>
          </div>
          <h1 className="relative flex items-center gap-2.5 text-2xl sm:text-3xl">
            <span
              aria-hidden
              className="flex size-10 items-center justify-center rounded-xl bg-brand text-white"
            >
              <SparkleIcon className="size-5" />
            </span>
            Plan my week
          </h1>
          <p className="relative max-w-2xl text-sm text-ink">
            Dinners that fit when you eat and how busy you are, favour what everyone likes, and
            share ingredients so you buy fewer bags.
          </p>
        </div>

        <section
          aria-label="Planner options"
          className="flex flex-col gap-3 card p-4 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="flex flex-col gap-2">
            <Field
              id="planner-from"
              label="Starting"
              type="date"
              value={from}
              onChange={(e) => changeRange(e.target.value)}
            />
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quick start dates">
              <button
                type="button"
                className={chipButton(from === today)}
                aria-pressed={from === today}
                onClick={() => changeRange(today)}
              >
                From today
              </button>
              {nextMonday !== today ? (
                <button
                  type="button"
                  className={chipButton(from === nextMonday)}
                  aria-pressed={from === nextMonday}
                  onClick={() => changeRange(nextMonday)}
                >
                  Next week
                </button>
              ) : null}
            </div>
          </div>
          <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={includeSides}
              onChange={(e) => {
                setIncludeSides(e.target.checked);
                setPins({});
              }}
              className="size-5 accent-brand"
            />
            Add a side dish to each dinner
          </label>
        </section>

        {proposal.data ? (
          <p className="text-sm text-muted" aria-live="polite">
            {formatWeekLabel(proposal.data.from, proposal.data.to)}
            {working ? ' · planning…' : ''}
          </p>
        ) : null}

        {proposal.isPending ? (
          <ul
            className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
            aria-busy="true"
            aria-label="Planning your week"
          >
            {Array.from({ length: 6 }, (_, i) => (
              <li key={i} className="skeleton h-56 rounded-2xl" />
            ))}
          </ul>
        ) : null}

        {proposal.isError ? (
          <Alert tone="error">
            {messageForError(proposal.error)}{' '}
            <button
              type="button"
              className="font-semibold underline"
              onClick={() => void proposal.refetch()}
            >
              Try again
            </button>
          </Alert>
        ) : null}

        {noRecipes ? (
          <Alert tone="info">
            There are no recipes to plan with yet.{' '}
            <Link to="/recipes/new" className="font-semibold text-brand underline">
              Add a recipe
            </Link>{' '}
            or rate a few shared ones, and the planner will fill your week.
          </Alert>
        ) : null}

        {proposal.data ? (
          <ol
            className={`grid gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-3 ${working ? 'opacity-70' : ''}`}
          >
            {days.map((day) => (
              <PlannerDayCard
                key={day.date}
                day={day}
                timezone={proposal.data.timezone}
                userLocked={Boolean(userLocks[day.date])}
                busy={working || accept.isPending}
                onSwap={() => swap(day)}
                onToggleLock={() => toggleLock(day)}
                onSkip={() => skipDay(day)}
                onRestore={() => restoreDay(day)}
              />
            ))}
          </ol>
        ) : null}

        {proposal.data ? (
          <section
            aria-label="Add to plan"
            className="sticky bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-10 flex flex-col gap-3 card border-brand/30 p-3 !shadow-[var(--shadow-lift)] sm:p-4 lg:bottom-4"
          >
            {accept.isError ? <Alert tone="error">{messageForError(accept.error)}</Alert> : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-2 text-sm font-medium text-ink">
                  <input
                    type="checkbox"
                    checked={buildList}
                    onChange={(e) => setBuildList(e.target.checked)}
                    className="size-5 accent-brand"
                  />
                  Also build the grocery list
                </label>
                {buildList ? (
                  <button
                    type="button"
                    className="self-start pl-7 text-xs font-semibold text-brand underline-offset-2 hover:underline"
                    onClick={() => setChoosingStore((v) => !v)}
                    aria-expanded={choosingStore}
                  >
                    {store ? `Store: ${store.chain} ${store.name}` : 'Choose a store (optional)'}
                  </button>
                ) : null}
              </div>
              <Button
                type="button"
                className="sm:w-auto"
                disabled={n === 0 || working || accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending
                  ? 'Adding…'
                  : n === 0
                    ? 'Nothing to add'
                    : `Add ${n} ${n === 1 ? 'meal' : 'meals'} to plan`}
              </Button>
            </div>
            {buildList && choosingStore ? (
              <StorePicker
                selectedId={store?.id ?? null}
                onPick={(picked) => {
                  setStore(picked);
                  setChoosingStore(false);
                }}
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </AppShell>
  );
}
